import { FC } from 'react';
import { Sparkles, BookOpen, ListPlus, LayoutDashboard } from 'lucide-react';
import { Button } from '@new/common/ui';
import { useAppUpdateModal } from '@new/features/announcement/hooks/useAppUpdateModal';

const NEW_FEATURES = [
  {
    icon: LayoutDashboard,
    title: 'Your learning, at a glance',
    description: 'See all your courses and track your progress',
  },
  {
    icon: BookOpen,
    title: 'A more intuitive design',
    description:
      'An improved course and lesson layout helps you stay focused on finishing each course.',
  },
  {
    icon: ListPlus,
    title: 'Flexibility built in',
    description:
      'Easily start, pause, add, or drop courses as life changes — pick up right where you left off.',
  },
];

export const AppUpdateModal: FC = () => {
  const { isOpen, onClose } = useAppUpdateModal();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-(--neutral-black-NB1000)/60 backdrop-blur-sm" />

      <div className="relative flex w-full max-w-xl flex-col gap-8 rounded-3xl bg-(--neutral-white-NW1000) p-4 lg:p-8 shadow-2xl">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-(--primary-O200)">
            <Sparkles className="h-8 w-8 text-(--primary-O900)" />
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="m-0! [font-size:var(--fs-h4)]! font-bold text-(--neutral-black-NB1000)! leading-tight [font-family:var(--font-arvo)]!">
              We just made Newlane better for you:
            </h2>
            <p className="m-0! [font-size:var(--fs-p3)]! text-(--neutral-black-NB600)! leading-tight">
              Newlane got a big upgrade. Here&apos;s what&apos;s waiting for you:
            </p>
          </div>
        </div>

        <ul className="flex flex-col gap-4 list-none m-0 p-0">
          {NEW_FEATURES.map(({ icon: Icon, title, description }) => (
            <li key={title} className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-(--primary-O200)">
                <Icon className="h-5 w-5 text-(--primary-O900)" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="[font-size:var(--fs-p4)]! font-semibold text-(--neutral-black-NB1000)! [font-family:var(--font-arvo)]!">
                  {title}
                </span>
                <span className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB600)! leading-snug">
                  {description}
                </span>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-3">
          <Button variant="primary" size="lg" className="w-full" onClick={onClose}>
            Let's go!
          </Button>
          <p className="m-0! text-center [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! leading-tight text-(--neutral-black-NB500)!">
            Log in or create an account to start learning.
          </p>
        </div>
      </div>
    </div>
  );
};
